import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Collapse,
  Table,
  Row,
  Col,
  Alert,
} from "reactstrap";
import DocumentPreview from "../../../../components/Documents/DocumentPreview";
import EsignService from "../../../../service/EsignService";
import documentsApi from "../../../../service/documentsApi";
import dealService from "../../../../service/dealService.ts";
import { useAuth } from "../../../../contexts/AuthContext";

const Document = ({
  documentEvent,
  file,
  title = "Document Details",
  alwaysExpanded = false,
  historyData = null,
  deal = null,
  documentType = "CONTRACT", // New prop to specify document type
}) => {
  const { getToken } = useAuth();
  const [isOpen, setIsOpen] = useState(alwaysExpanded);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [autoUploadAttempted, setAutoUploadAttempted] = useState(false);
  const [statusUpdateAttempted, setStatusUpdateAttempted] = useState(false);

  // Get document type label for display
  const getDocumentTypeLabel = () => {
    return documentType === "ADDENDUM" ? "Addendum" : "Contract";
  };

  const toggleDocument = () => {
    if (!alwaysExpanded) {
      setIsOpen(!isOpen);
    }
  };

  const formatDateTime = (dateTime) => {
    try {
      const date = new Date(dateTime);
      return date.toLocaleString();
    } catch {
      return dateTime;
    }
  };

  // Get the current status from the first element in the activities array
  const getCurrentStatusFromHistory = () => {
    // Use the new payload structure with activities array (first element has latest status)
    if (historyData?.activities && historyData.activities.length > 0) {
      const firstActivity = historyData.activities[0];
      console.log("Using first activity status:", firstActivity.status);
      return firstActivity.status?.toUpperCase() || "N/A";
    }

    // Fallback to envelope status if no activities
    if (historyData?.envelopeInfo?.status) {
      console.log(
        "Using envelope status as fallback:",
        historyData.envelopeInfo.status
      );
      return historyData.envelopeInfo.status.toUpperCase();
    }

    // Final fallback
    return documentEvent?.status?.toUpperCase() || "N/A";
  };

  // Get the current status from the envelopeInfo in the new payload structure
  const getCurrentStatus = () => {
    // Use the new payload structure with envelopeInfo.status
    if (historyData?.envelopeInfo?.status) {
      console.log("Using envelope status:", historyData.envelopeInfo.status);
      return historyData.envelopeInfo.status.toUpperCase();
    }

    // Final fallback
    return documentEvent?.status?.toUpperCase() || "N/A";
  };

  // Check if contract is completed (signed) based on history data
  const isContractCompleted = () => {
    console.log(
      "Checking if contract is completed. History data:",
      historyData
    );
    console.log("Contract Event:", documentEvent);
    console.log("Deal data:", deal);

    if (historyData?.activities && historyData.activities.length > 0) {
      const hasCompletedActivity = historyData.activities.some((activity) => {
        const isCompleted =
          activity.status && activity.status.toLowerCase() === "completed";
        const isSigned =
          activity.action && activity.action.toLowerCase() === "signed";
        console.log(
          "Activity:",
          activity.action,
          "Status:",
          activity.status,
          "Is Completed:",
          isCompleted,
          "Is Signed:",
          isSigned
        );
        return isCompleted || isSigned;
      });

      if (hasCompletedActivity) {
        console.log("Contract is completed based on activities");
        return true;
      }
    }

    // // First check if any activity shows "Completed" status (most reliable indicator)
    // if (historyData?.activities && historyData.activities.length > 0) {
    //   const hasCompletedActivity = historyData.activities.some((activity) => {
    //     const isCompleted =
    //       activity.status && activity.status.toLowerCase() === "completed";
    //     const isSigned =
    //       activity.action && activity.action.toLowerCase() === "signed";
    //     console.log(
    //       "Activity:",
    //       activity.action,
    //       "Status:",
    //       activity.status,
    //       "Is Completed:",
    //       isCompleted,
    //       "Is Signed:",
    //       isSigned
    //     );
    //     return isCompleted || isSigned;
    //   });

    //   if (hasCompletedActivity) {
    //     console.log(
    //       "Contract is completed based on activities - will auto-upload"
    //     );
    //     return true;
    //   }
    // }

    // Fallback to envelope status
    if (historyData?.envelopeInfo?.status) {
      const isCompleted =
        historyData.envelopeInfo.status.toLowerCase() === "completed";
      console.log(
        "Using envelope status:",
        historyData.envelopeInfo.status,
        "Is Completed:",
        isCompleted
      );
      return isCompleted;
    }

    // Final fallback - check contractEvent status
    if (documentEvent?.status) {
      const isCompleted = documentEvent.status.toLowerCase() === "completed";
      console.log(
        "Using contractEvent status:",
        documentEvent.status,
        "Is Completed:",
        isCompleted
      );
      return isCompleted;
    }

    console.log("No history data available, defaulting to false");
    return false;
  };

  // Function to update deal status to PRE_FUNDING when contract is completed
  const updateDealStatusToPreFunding = async () => {
    if (!deal?.privateId) {
      console.error("Missing deal privateId for status update");
      return;
    }

    // Only update if current status is CONTRACT
    if (deal?.status !== "CONTRACT") {
      console.log(
        "Deal status is not CONTRACT, skipping update. Current status:",
        deal?.status
      );
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      console.log(
        "Updating deal status from CONTRACT to PRE_FUNDING for deal:",
        deal.privateId
      );
      await dealService.updateDealStatus(token, deal.privateId, "PRE_FUNDING");
      await dealService.updateDealSubstatus(token, deal.privateId, "PENDING_PLAN");
      console.log("Deal status successfully updated to PRE_FUNDING");
    } catch (error) {
      console.error("Error updating deal status to PRE_FUNDING:", error);
    }
  };

  const loadContractPreview = async (envelopeId) => {
    if (!envelopeId) return;

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      console.log("Loading contract preview for envelope ID:", envelopeId);
      const response = await EsignService.downloadCompletedContractByEnvelopId(
        token,
        envelopeId
      );

      // Create blob URL for preview
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);

      console.log("Contract preview loaded successfully");
    } catch (error) {
      console.error("Error loading contract preview:", error);
      setPreviewError(error.message || "Failed to load contract preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Function to automatically create document copy in document storage (works for both contract and addendum)
  const autoUploadDocumentToStorage = async () => {
    if (!deal?.publicId || !documentEvent?.envelopeId) {
      console.error("Missing required data for automatic upload:", {
        envelopeId: documentEvent?.envelopeId,
        dealPublicId: deal?.publicId,
      });
      return;
    }

    // Prevent multiple upload attempts
    if (autoUploadAttempted) {
      console.log("Auto upload already attempted, skipping...");
      return;
    }

    setAutoUploadAttempted(true);
    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(false);

    const documentTypeLabel = getDocumentTypeLabel().toLowerCase();

    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      console.log(`Auto-downloading ${documentTypeLabel} for creating copy...`);
      // First, get the document blob from EsignService
      const documentResponse =
        await EsignService.downloadCompletedContractByEnvelopId(
          token,
          documentEvent.envelopeId
        );
      const documentBlob = documentResponse.data;

      // Create a File object from the blob
      const documentFile = new File(
        [documentBlob],
        `${documentTypeLabel}-${deal.publicId}.pdf`,
        { type: "application/pdf" }
      );

      console.log(
        `Auto-creating ${documentTypeLabel} copy in document service...`,
        {
          fileName: documentFile.name,
          size: documentFile.size,
          dealPublicId: deal.publicId,
        }
      );

      // Upload to document service with required form data
      const uploadResult = await documentsApi.uploadDocument({
        file: documentFile,
        category: "deal",
        originType: "deal",
        originId: deal.publicId,
        description: `Docusign signed ${documentTypeLabel} copy`,
        tags: `${documentTypeLabel},docusign,signed`,
      });

      console.log("Auto upload result:", uploadResult);

      // Check if the response is a Long (duplicate document ID)
      if (uploadResult && typeof uploadResult.documentId === "number") {
        console.log(
          "Duplicate document detected, using existing document ID:",
          uploadResult.documentId
        );
        setDocumentId(uploadResult.documentId);
        setUploadSuccess(true);
        // Load preview from existing document
        await loadPreviewFromDocumentId(uploadResult.documentId);
      } else if (uploadResult && uploadResult.documentId) {
        console.log(
          "New document created successfully:",
          uploadResult.documentId
        );
        setDocumentId(uploadResult.documentId);
        setUploadSuccess(true);
        // Load preview from new document
        await loadPreviewFromDocumentId(uploadResult.documentId);
      } else {
        console.log(
          `${getDocumentTypeLabel()} copy created successfully, no document ID returned`
        );
        setUploadSuccess(true);
      }
    } catch (error) {
      console.error("Error in auto upload:", error);
      setUploadError(
        error.message ||
          `Failed to create ${documentTypeLabel} copy automatically`
      );
    } finally {
      setUploadLoading(false);
    }
  };

  // Function to load preview from document ID using documentApi
  const loadPreviewFromDocumentId = async (docId) => {
    try {
      console.log("Loading preview from document ID:", docId);
      setPreviewLoading(true);
      setPreviewError(null);

      const previewResult = await documentsApi.getPreviewResource(docId);
      console.log("Preview result:", previewResult);

      if (previewResult.objectUrl) {
        // If we get a blob with objectUrl, use it directly
        setPreviewUrl(previewResult.objectUrl);
      } else if (previewResult.url) {
        // If we get a presigned URL, use it
        setPreviewUrl(previewResult.url);
      } else {
        throw new Error("No preview URL available from document service");
      }

      console.log("Preview loaded successfully from document service");
    } catch (error) {
      console.error("Error loading preview from document ID:", error);
      setPreviewError(
        error.message || "Failed to load preview from document service"
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  // Load preview when document preview is opened
  const toggleDocumentPreview = () => {
    const newShowState = !showDocumentPreview;
    setShowDocumentPreview(newShowState);

    // Load preview when opening, clean up when closing
    if (newShowState && !previewUrl) {
      if (documentId) {
        // If we have a document ID from upload, use it
        loadPreviewFromDocumentId(documentId);
      } else if (documentEvent?.envelopeId) {
        // Otherwise, load from EsignService as fallback
        loadContractPreview(documentEvent.envelopeId);
      }
    } else if (!newShowState && previewUrl) {
      // Clean up blob URL when closing
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setPreviewError(null);
    }
  };

  // Auto upload and status update effect - triggers when document is completed
  React.useEffect(() => {
    const documentCompleted = isContractCompleted();

    if (documentCompleted) {
      // Update deal status to PRE_FUNDING if not already attempted (only for contracts)
      if (
        !statusUpdateAttempted &&
        deal?.privateId &&
        documentType === "CONTRACT"
      ) {
        console.log(
          "Contract is completed, updating deal status to PRE_FUNDING..."
        );
        setStatusUpdateAttempted(true);
        updateDealStatusToPreFunding();
      }

      // Start automatic upload if not already attempted
      if (!autoUploadAttempted && deal?.publicId && documentEvent?.envelopeId) {
        console.log(
          `${getDocumentTypeLabel()} is completed, starting automatic upload...`
        );
        autoUploadDocumentToStorage();
      }
    }
  }, [
    documentEvent,
    historyData,
    deal,
    autoUploadAttempted,
    statusUpdateAttempted,
    documentType,
  ]);

  return (
    <Card className="shadow-sm mb-3">
      {!alwaysExpanded && (
        <CardHeader
          className="d-flex justify-content-between align-items-center cursor-pointer"
          onClick={toggleDocument}
          style={{
            cursor: "pointer",
            backgroundColor: isOpen ? "#f8f9fa" : "#fff",
          }}
        >
          <div className="d-flex align-items-center">
            <i className="ni ni-single-copy-04 text-primary mr-2" />
            <h5 className="mb-0">{title}</h5>
            {file && (
              <Badge color="info" className="ml-2">
                {typeof file === "string" ? file : file.name}
              </Badge>
            )}
          </div>
          <Button color="link" size="sm" className="p-0">
            <i
              className={`ni ${isOpen ? "ni-fat-remove" : "ni-fat-add"}`}
              style={{ fontSize: "1.0rem" }}
            />
          </Button>
        </CardHeader>
      )}
      <Collapse isOpen={isOpen}>
        <CardBody className="p-4">
          {/* Contract Information Section */}
          <div className="mb-4">
            <h6 className="text-muted small font-weight-bold mb-3">
              DOCUMENT INFORMATION
            </h6>
            <Row>
              <Col md="6" className="mb-3">
                <label className="form-label text-muted small font-weight-bold">
                  Document Type
                </label>
                <div
                  className="form-control bg-light border-0"
                  style={{
                    backgroundColor: "#e9ecef",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                    minHeight: "auto",
                    height: "auto",
                  }}
                >
                  {getDocumentTypeLabel()}
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <label className="form-label text-muted small font-weight-bold">
                  Status
                </label>
                <div
                  className="form-control bg-light border-0"
                  style={{
                    backgroundColor: "#e9ecef",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                    minHeight: "auto",
                    height: "auto",
                  }}
                >
                  {getCurrentStatusFromHistory()}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md="6" className="mb-3">
                <label className="form-label text-muted small font-weight-bold">
                  Email Subject
                </label>
                <div
                  className="form-control bg-light border-0"
                  style={{
                    backgroundColor: "#e9ecef",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                    minHeight: "auto",
                    height: "auto",
                  }}
                >
                  {documentEvent ? documentEvent.emailSubject : "N/A"}
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <label className="form-label text-muted small font-weight-bold">
                  Created Date
                </label>
                <div
                  className="form-control bg-light border-0"
                  style={{
                    backgroundColor: "#e9ecef",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                    minHeight: "auto",
                    height: "auto",
                  }}
                >
                  {documentEvent
                    ? formatDateTime(documentEvent.createdDate)
                    : "N/A"}
                </div>
              </Col>
            </Row>
          </div>

          {/* Document Preview Section - Only show if contract is completed */}
          {isContractCompleted() && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-muted small font-weight-bold mb-0">
                  DOCUMENT PREVIEW
                </h6>
                <div className="d-flex align-items-center">
                  {/* Auto Upload Status Indicator */}
                  {uploadLoading && (
                    <div className="d-flex align-items-center mr-2 text-info">
                      <i
                        className="ni ni-settings-gear-65"
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      <span className="ml-1 small">Auto-creating copy...</span>
                    </div>
                  )}

                  {uploadSuccess && documentId && (
                    <div className="d-flex align-items-center mr-2 text-success">
                      <i className="ni ni-check-bold" />
                      <span className="ml-1 small">
                        Copy stored (ID: {documentId})
                      </span>
                    </div>
                  )}

                  {/* Toggle Preview Button */}
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={toggleDocumentPreview}
                  >
                    <i
                      className={`ni ${
                        showDocumentPreview ? "ni-fat-remove" : "ni-fat-add"
                      }`}
                      style={{ fontSize: "1.0rem" }}
                    />
                  </Button>
                </div>
              </div>

              {/* Auto Upload Status Messages */}
              {uploadError && (
                <Alert color="danger" className="mb-3">
                  <i className="ni ni-fat-remove mr-2" />
                  Auto copy creation failed: {uploadError}
                  <Button
                    color="outline-danger"
                    size="sm"
                    className="ml-2"
                    onClick={() => {
                      console.log("Retry auto upload clicked!");
                      setAutoUploadAttempted(false);
                      autoUploadDocumentToStorage();
                    }}
                    disabled={uploadLoading}
                  >
                    Retry Auto Upload
                  </Button>
                </Alert>
              )}

              <Collapse isOpen={showDocumentPreview}>
                <div
                  className="border rounded"
                  style={{ backgroundColor: "#f8f9fa" }}
                >
                  {documentEvent?.envelopeId ? (
                    <>
                      {/* Loading State */}
                      {previewLoading && (
                        <div className="text-center py-5">
                          <i
                            className="ni ni-settings-gear-65 text-primary"
                            style={{
                              fontSize: "2rem",
                              animation: "spin 2s linear infinite",
                            }}
                          />
                          <p className="mt-2 mb-0">
                            Loading {getDocumentTypeLabel().toLowerCase()}{" "}
                            preview...
                          </p>
                        </div>
                      )}

                      {/* Error State */}
                      {previewError && (
                        <div className="text-center py-5">
                          <i
                            className="ni ni-fat-remove text-danger"
                            style={{ fontSize: "2rem" }}
                          />
                          <h6 className="mt-3 text-danger">Preview Error</h6>
                          <p className="text-muted mb-3">{previewError}</p>
                          <Button
                            color="outline-primary"
                            size="sm"
                            onClick={() =>
                              loadContractPreview(documentEvent.envelopeId)
                            }
                          >
                            Retry
                          </Button>
                        </div>
                      )}

                      {/* PDF Preview */}
                      {previewUrl && !previewLoading && !previewError && (
                        <div style={{ height: "500px" }}>
                          <iframe
                            src={previewUrl}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              borderRadius: "4px",
                            }}
                            title="Contract Preview"
                          />
                        </div>
                      )}

                      {/* Initial State - No preview loaded yet */}
                      {!previewLoading && !previewError && !previewUrl && (
                        <div className="text-center py-5">
                          <i
                            className="ni ni-paper-diploma text-primary"
                            style={{ fontSize: "3rem" }}
                          />
                          <h6 className="mt-3 text-primary">
                            Contract Preview
                          </h6>
                          <p className="text-muted mb-0">
                            Preview will load when you expand this section
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <i
                        className="ni ni-folder-17 text-muted"
                        style={{ fontSize: "3rem" }}
                      />
                      <h6 className="mt-3 text-muted">No Document Available</h6>
                      <p className="text-muted mb-0">
                        There is no document associated with this contract
                      </p>
                    </div>
                  )}
                </div>
              </Collapse>
            </div>
          )}
        </CardBody>
      </Collapse>
    </Card>
  );
};

export default Document;
